import { Link } from "react-router";

import { longDateFormat } from "@/utils/date";

import { getWritingCategory } from "./categories";
import type { WritingPostSummary } from "./post-schema";

interface WritingPostListProps {
  posts: readonly WritingPostSummary[];
}

// Present writing summaries consistently on the complete and category archives.
export function WritingPostList({ posts }: WritingPostListProps) {
  if (posts.length === 0) {
    return (
      <p className="text-muted-foreground mt-10 leading-7">
        No writing has been published here yet.
      </p>
    );
  }

  return (
    <ol className="mt-10">
      {posts.map((post) => {
        const category = getWritingCategory(post.category);

        return (
          <li key={post.id} className="border-t first:border-t-0">
            <article className="grid gap-3 py-7 sm:grid-cols-[9rem_minmax(0,1fr)_auto] sm:gap-6 sm:py-8">
              <div className="text-muted-foreground flex items-center gap-3 font-mono text-xs sm:block">
                <Link
                  className="hover:text-primary font-semibold tracking-[0.08em] uppercase"
                  to={`/writing/categories/${post.category}`}
                >
                  {category?.label ?? post.category}
                </Link>
                <span aria-hidden="true" className="sm:hidden">
                  ·
                </span>
                <time className="sm:mt-2 sm:block" dateTime={post.published}>
                  {longDateFormat(post.published)}
                </time>
              </div>

              <div>
                <h2 className="font-serif text-2xl font-semibold tracking-[-0.02em]">
                  <Link
                    className="hover:text-primary transition-colors"
                    to={`/writing/${post.slug}`}
                  >
                    {post.title}
                  </Link>
                </h2>
                <p className="text-muted-foreground mt-3 max-w-2xl leading-7">
                  {post.description}
                </p>
              </div>

              <Link
                aria-label={`Read ${post.title}`}
                className="text-muted-foreground hover:text-primary hidden pt-1 text-lg transition-[color,transform] hover:translate-x-0.5 sm:block"
                to={`/writing/${post.slug}`}
              >
                →
              </Link>
            </article>
          </li>
        );
      })}
    </ol>
  );
}
